import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';

export interface Panel<T> {
  loading: boolean;
  error: string | null;
  data: T | null;
}

export interface FleetPressure {
  total: number;
  active: number;
  oos: number;
  in_transit: number;
  openIssues: number;
  vehiclesWithIssues: number;
}

export interface SafetyCompliance {
  loaded: number;
  total: number;
  open: number;
  closed: number;
  terminalsCovered: number;
  latestOpenedAt: number | null;
  daysSinceLatest: number | null;
}

export interface FinanceHealth {
  total: number;
  verified: number;
  pending: number;
  missing: number;
  variance: number;
}

function emptyPanel<T>(): Panel<T> {
  return { loading: true, error: null, data: null };
}

function errorPanel<T>(message: string): Panel<T> {
  return { loading: false, error: message || 'Unavailable', data: null };
}

function okPanel<T>(data: T): Panel<T> {
  return { loading: false, error: null, data };
}

interface FleetVehicle {
  current_status?: string;
  issue_count?: number | string;
}

function deriveFleetPressure(payload: unknown): FleetPressure {
  const raw = payload as { vehicles?: FleetVehicle[] } | null;
  const vehicles = Array.isArray(raw?.vehicles) ? raw!.vehicles : [];
  const counts: FleetPressure = { total: vehicles.length, active: 0, oos: 0, in_transit: 0, openIssues: 0, vehiclesWithIssues: 0 };
  vehicles.forEach((v) => {
    const s = v.current_status;
    if (s === 'active') counts.active++;
    else if (s === 'oos') counts.oos++;
    else if (s === 'in_transit') counts.in_transit++;
    const issues = Number(v.issue_count) || 0;
    counts.openIssues += issues;
    if (issues > 0) counts.vehiclesWithIssues++;
  });
  return counts;
}

interface SafetyMeeting {
  terminal_number?: string;
  status?: string;
  opened_at?: string;
}

function deriveSafetyCompliance(payload: unknown): SafetyCompliance {
  const raw = payload as { meetings?: SafetyMeeting[]; total?: number } | null;
  const meetings = Array.isArray(raw?.meetings) ? raw!.meetings : [];
  const total = typeof raw?.total === 'number' ? raw.total : meetings.length;

  const terminals = new Set<string>();
  let open = 0;
  let closed = 0;
  let latestOpenedAt: number | null = null;

  meetings.forEach((m) => {
    if (m.terminal_number) terminals.add(String(m.terminal_number));
    const status = String(m.status || '').toLowerCase();
    if (status === 'open') open++;
    else if (status === 'closed') closed++;
    if (m.opened_at) {
      const t = new Date(m.opened_at).getTime();
      if (!Number.isNaN(t) && (latestOpenedAt == null || t > latestOpenedAt)) {
        latestOpenedAt = t;
      }
    }
  });

  let daysSinceLatest: number | null = null;
  if (latestOpenedAt != null) {
    daysSinceLatest = Math.floor((Date.now() - latestOpenedAt) / (24 * 60 * 60 * 1000));
  }

  return { loaded: meetings.length, total, open, closed, terminalsCovered: terminals.size, latestOpenedAt, daysSinceLatest };
}

interface VerificationRow {
  status?: string;
}

function deriveFinanceHealth(payload: unknown): FinanceHealth {
  const raw = payload as { verifications?: VerificationRow[] } | null;
  const verifications = Array.isArray(raw?.verifications) ? raw!.verifications : [];
  const counts: FinanceHealth = { total: verifications.length, verified: 0, pending: 0, missing: 0, variance: 0 };
  verifications.forEach((v) => {
    const s = String(v.status || '').toLowerCase();
    if (s === 'verified') counts.verified++;
    else if (s === 'pending') counts.pending++;
    else if (s === 'missing') counts.missing++;
    else if (s === 'variance') counts.variance++;
  });
  return counts;
}

async function fetchPanel<T>(path: string, derive: (data: unknown) => T): Promise<Panel<T>> {
  try {
    const data = await apiFetch(path);
    if (data && typeof data === 'object' && (data as Record<string, unknown>).error) {
      return errorPanel<T>(String((data as Record<string, unknown>).error));
    }
    return okPanel(derive(data));
  } catch (err) {
    return errorPanel<T>(err instanceof Error ? err.message : 'Network error');
  }
}

export function useOpsReadModel() {
  const [fleet, setFleet] = useState<Panel<FleetPressure>>(emptyPanel());
  const [safety, setSafety] = useState<Panel<SafetyCompliance>>(emptyPanel());
  const [finance, setFinance] = useState<Panel<FinanceHealth>>(emptyPanel());

  const load = useCallback(async () => {
    setFleet(emptyPanel());
    setSafety(emptyPanel());
    setFinance(emptyPanel());

    const [fleetResult, safetyResult, financeResult] = await Promise.all([
      fetchPanel('/api/fleet/vehicles', deriveFleetPressure),
      fetchPanel('/api/safety-meeting/history?limit=50', deriveSafetyCompliance),
      fetchPanel('/api/finance/settlement-verification?period=last_week', deriveFinanceHealth),
    ]);

    setFleet(fleetResult);
    setSafety(safetyResult);
    setFinance(financeResult);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { fleet, safety, finance, refreshOps: load };
}
